<template>
  <div v-if="isEnabled" class="genai-section d-flex gap-6">
    <!-- Proforma Accordion Panel -->
    <div 
      class="genai-accordion-panel" 
      :class="{ 'is-expanded': proformaExpanded }" 
      style="flex: 1;"
    >
      <div 
        class="genai-accordion-header d-flex align-center justify-space-between cursor-pointer pa-3"
        @click="proformaExpanded = !proformaExpanded"
      >
        <h5 class="genai-accordion-title">{{ proformaLabel }}</h5>
        <v-spacer></v-spacer>
        <v-icon 
          class="accordion-icon mr-2" 
          :class="{ 'is-rotated': proformaExpanded }" 
          size="20"
        >
          mdi-chevron-right
        </v-icon>
        <v-spacer></v-spacer>
      </div>
      <v-expand-transition>
        <div v-show="proformaExpanded" class="genai-accordion-content pa-3">
          <div v-if="loadingProforma" class="pa-3">
            <v-skeleton-loader
              type="paragraph,paragraph,paragraph"
              class="skeleton-textarea"
            ></v-skeleton-loader>
          </div>
          <div v-else>
            <v-textarea
              outlined
              :value="valueProforma"
              @input="$emit('update:valueProforma', $event)"
              :label="textareaLabel"
              rows="6"
              hide-details
              class="genai-textarea"
            ></v-textarea>
            <div class="d-flex justify-end mt-3">
              <v-btn
                color="primary"
                :disabled="isTyping || loadingProforma || loadingForecast || isThresholdExceeded"
                @click.stop="$emit('generate-ai', 'One', 'proforma')"
              >
                <v-icon dark left small>mdi-creation</v-icon>
                <span class="noTransform">Gen AI</span>
              </v-btn>
            </div>
          </div>
        </div>
      </v-expand-transition>
    </div>

    <!-- Forecast Accordion Panel -->
    <div 
      class="genai-accordion-panel" 
      :class="{ 'is-expanded': forecastExpanded }" 
      style="flex: 1;"
    >
      <div 
        class="genai-accordion-header d-flex align-center justify-space-between cursor-pointer pa-3"
        @click="forecastExpanded = !forecastExpanded"
      >
        <h5 class="genai-accordion-title">{{ forecastLabel }}</h5>
        <v-spacer></v-spacer>
        <v-icon 
          class="accordion-icon mr-2" 
          :class="{ 'is-rotated': forecastExpanded }" 
          size="20"
        >
          mdi-chevron-right
        </v-icon>
        <v-spacer></v-spacer>
      </div>
      <v-expand-transition>
        <div v-show="forecastExpanded" class="genai-accordion-content pa-3">
          <div v-if="loadingForecast" class="pa-3">
            <v-skeleton-loader
              type="paragraph,paragraph,paragraph"
              class="skeleton-textarea"
            ></v-skeleton-loader>
          </div>
          <div v-else>
            <v-textarea
              outlined
              :value="valueForecast"
              @input="$emit('update:valueForecast', $event)"
              :label="textareaLabel"
              rows="6"
              hide-details
              class="genai-textarea"
            ></v-textarea>
            <div class="d-flex justify-end mt-3">
              <v-btn
                color="primary"
                :disabled="isTyping || loadingProforma || loadingForecast || isThresholdExceeded"
                @click.stop="$emit('generate-ai', 'Two', 'forecast')"
              >
                <v-icon dark left small>mdi-creation</v-icon>
                <span class="noTransform">Gen AI</span>
              </v-btn>
            </div>
          </div>
        </div>
      </v-expand-transition>
    </div>
  </div>
</template>

<script>
/**
 * GenAICommentarySection Component
 * 
 * Reusable accordion section for Gen AI commentary generation.
 * Used in RevenueBreakup and NetSalesBreakup pages.
 * 
 * Features:
 * - Two accordion panels for Proforma and Forecast commentary
 * - Smooth expand/collapse animations
 * - Loading skeleton states
 * - Gen AI button with disabled states
 */
export default {
  name: 'GenAICommentarySection',
  
  props: {
    /** Whether the Gen AI section is enabled/visible */
    isEnabled: {
      type: Boolean,
      default: false
    },
    /** Proforma textarea value (use with .sync) */
    valueProforma: {
      type: String,
      default: ''
    },
    /** Forecast textarea value (use with .sync) */
    valueForecast: {
      type: String,
      default: ''
    },
    /** Loading state for proforma generation */
    loadingProforma: {
      type: Boolean,
      default: false
    },
    /** Loading state for forecast generation */
    loadingForecast: {
      type: Boolean,
      default: false
    },
    /** Whether AI is currently typing */
    isTyping: {
      type: Boolean,
      default: false
    },
    /** Whether threshold is exceeded for Gen AI */
    isThresholdExceeded: {
      type: Boolean,
      default: false
    },
    /** Label for proforma accordion panel */
    proformaLabel: {
      type: String,
      default: 'Local Commentary (Vs Proforma)'
    },
    /** Label for forecast accordion panel */
    forecastLabel: {
      type: String,
      default: 'Local Commentary (Vs Forecast)'
    },
    /** Placeholder label for textarea */
    textareaLabel: {
      type: String,
      default: 'Text here'
    }
  },
  
  data() {
    return {
      proformaExpanded: true,
      forecastExpanded: true
    };
  }
};
</script>

<style scoped lang="scss">
.genai-section {
  width: 100%;
}

.genai-accordion-panel {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.genai-accordion-header {
  border-bottom: 1px solid transparent;
  transition: all 0.2s ease;
}

.is-expanded .genai-accordion-header {
  border-bottom-color: #e0e0e0;
}

.genai-accordion-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary, #1f2937);
  margin: 0;
}

.genai-accordion-content {
  transition: all 0.3s ease-in-out;
}

// Icon rotation animation
.accordion-icon {
  transition: transform 0.3s ease-in-out;
  
  &.is-rotated {
    transform: rotate(90deg);
  }
}

// Smooth expand transition
.v-expand-transition-enter-active,
.v-expand-transition-leave-active {
  transition: all 0.3s ease-in-out !important;
}

// Content fade effect
.genai-accordion-content > * {
  transition: opacity 0.2s ease-in-out 0.1s;
}

.genai-textarea {
  ::v-deep .v-input__slot {
    background: #ffffff !important;
    border-radius: 6px;
  }
  
  ::v-deep textarea {
    font-size: 14px;
    line-height: 1.6;
  }
}

.skeleton-textarea {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
}

.noTransform {
  text-transform: none;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
